import { useState } from "react";
import {
  CreditCard,
  Shield,
  Clock,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import axios from "axios";

import Api from "../Api/Api.json";

const Payment = () => {
  const [cost, setCost] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const grantToken = async () => {
    setIsConfirming(true);
    try {
      const body = { task: "grantToken" };
      const headers = { "Content-Type": "application/json" };
      const res = await axios.post(Api.PROVIDER_API, body, { headers });

      if (!res.data.errorMessage) {
        console.log("Token granted successfully:", res.data);
        createPayment(res.data);
      } else {
        throw new Error("Token error");
      }
    } catch (err) {
      console.error("Error granting token:", err);
      setIsConfirming(false);
    }
  };

  const createPayment = async (token_id) => {
    const patientId = JSON.parse(localStorage.getItem("user_info"))?.di_id;
    const doctor_id = 0;
    const rateField = 0;
    const num = patientId + doctor_id + new Date().getTime();

    const body = {
      task: "createPaymentBackend",
      amount: cost.toString(),
      merchantInvoiceNumber: num.toString(),
      token_id,
      rateField: rateField.toString(),
      doctor_id: doctor_id.toString(),
      patient_id: patientId.toString(),
    };

    try {
      const headers = { "Content-Type": "application/json" };
      const res = await axios.post(Api.PROVIDER_API, body, { headers });

      if (!res.data.errorMessage && res.data.message !== "Unauthorized") {
        const matches =
          res.data.merchantInvoiceNumber === num &&
          res.data.amount.toString() === cost.toString() &&
          res.data.intent === "sale";

        if (matches) {
          window.location = res.data.bkashURL;
        } else {
          console.error("Payment details do not match");
        }
      } else {
        console.error("Unauthorized access or error in payment creation");
      }
    } catch (err) {
      console.error("Error creating payment:", err);
    } finally {
      setIsConfirming(false);
    }
  };

  const handlePayment = () => {
    if (cost <= 0) {
      alert("Please enter a valid amount");
      return;
    }
    grantToken();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        {/* Main Payment Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CreditCard className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Secure Payment</h2>
                <p className="text-blue-100 text-sm">
                  Complete your transaction
                </p>
              </div>
            </div>
          </div>

          {/* Payment Content */}
          <div className="p-6 space-y-6">
            {/* Amount Input */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Payment Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                  ৳
                </span>
                <input
                  type="number"
                  value={cost}
                  onChange={(e) => setCost(Number(e.target.value))}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-lg font-semibold"
                  placeholder="0.00"
                  min="0"
                  step="1"
                />
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                Payment Method
              </label>
              <div className="grid grid-cols-1 gap-3">
                <div
                  className="p-4 border-2 rounded-xl cursor-pointer transition-all duration-200border-pink-500 bg-pink-50"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-pink-500 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">bK</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">bKash</p>
                      <p className="text-sm text-gray-500">
                        Mobile Financial Service
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Features */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    Secure Payment
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Your payment is protected with bank-level security and
                    encryption
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Summary */}
            {cost > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Amount:</span>
                  <span className="text-lg font-bold text-blue-600">
                    ৳{cost.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Payment Button */}
            <button
              onClick={handlePayment}
              disabled={isConfirming || cost <= 0}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-300 transform ${
                isConfirming || cost <= 0
                  ? "bg-gray-400 cursor-not-allowed scale-95"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 shadow-lg hover:shadow-xl"
              }`}
            >
              {isConfirming ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing Payment...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <CreditCard className="w-5 h-5" />
                  <span>Pay with bKash</span>
                </div>
              )}
            </button>

            {/* Payment Steps Indicator */}
            {isConfirming && (
              <div className="space-y-3 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-3 text-sm">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-600">
                    Validating payment details
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <div className="w-6 h-6 border-2 border-blue-600 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                  </div>
                  <span className="text-gray-600">
                    Connecting to payment gateway
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-sm opacity-50">
                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full flex items-center justify-center">
                    <Clock className="w-4 h-4 text-gray-400" />
                  </div>
                  <span className="text-gray-400">Redirecting to bKash</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>Powered by bKash • Secure Payment Gateway</p>
          <p className="mt-1">Your transaction is protected and encrypted</p>
        </div>
      </div>
    </div>
  );
};

export default Payment;
